// Notification.tsx
import DoneIcon from '@mui/icons-material/Done'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Box, IconButton, Link as MuiLink, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Notify } from 'types'
import { boardViewMapping } from 'utils/boardViewMapping'
import { formatDateTime } from 'utils/formatDayjsDateTime'
import { useChangeView } from 'utils/useChangeView'
import RenderTypeComponent from './RenderTypeIcon'

const Notification = ({
  notification,
  url,
  marked,
  onMarkRead,
  closeDrawer,
}: {
  notification: Notify
  url: string
  marked: boolean
  onMarkRead: (n: any) => void
  closeDrawer?: () => void
}) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const handleChangeView = useChangeView()
  const { message, severity, title, type, links, origin, board } = notification ?? {}
  const timestamp = notification?.timestamp || notification?.creation_timestamp

  const localLinks = useMemo(() => {
    if (!links) return []
    return links ?? []
  }, [links])

  const handleNavigate = (to?: string) => {
    const dest = to || url || '/teamtasks'
    // keep your route mapping:
    const route = dest.includes('nco')
      ? '/nco'
      : dest.includes('projects')
      ? '/projects'
      : dest.includes('mtb')
      ? '/mtb'
      : '/teamtasks'

    const newView = boardViewMapping[route]
    if (newView) handleChangeView({ newView })
    closeDrawer()
    navigate(dest)
  }

  const severityColor = (() => {
    switch (severity) {
      case 'warning':
        return theme.palette.warning.main
      case 'success':
        return theme.palette.success.main
      case 'prio':
      case 'error':
        return theme.palette.error.main
      default:
        return theme.palette.text.primary
    }
  })()

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        p: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: marked ? 'divider' : severityColor,
        backgroundColor: marked ? 'action.hover' : 'background.paper',
        transition: 'transform 120ms ease, box-shadow 120ms ease',
        '&:hover': { boxShadow: 4, transform: 'translateY(-1px)' },
      }}
    >
      <RenderTypeComponent type={type} origin={origin ?? board ?? null} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {!!title && (
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.25 }}>
            {title}
          </Typography>
        )}

        {/* Main message */}
        {message && (
          <Typography
            variant="body2"
            color="text.primary"
            sx={{ wordBreak: 'break-word', mb: links?.length ? 0.75 : 0 }}
          >
            {message}
          </Typography>
        )}

        {/* NEW: render a neat list of task links if provided */}
        {!!links?.length && (
          <Stack spacing={0.5} sx={{ mt: 0.25 }}>
            {links.map((l, idx) => (
              <MuiLink
                key={idx}
                component="button"
                type="button"
                onClick={e => {
                  e.stopPropagation()
                  handleNavigate(l.url)
                }}
                underline="hover"
                sx={{ textAlign: 'left', fontSize: 14, color: 'text.highlight' }}
              >
                {l.label}
              </MuiLink>
            ))}
          </Stack>
        )}

        {/* Timestamp */}
        {timestamp && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {formatDateTime(timestamp)}
          </Typography>
        )}
      </Box>

      {/* Actions */}
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Tooltip title={marked ? 'Marked' : 'Mark as read'}>
          <IconButton size="small" onClick={onMarkRead} aria-label="mark as read">
            <DoneIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Open">
          <span>
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation()
                handleNavigate() // default url or /teamtasks
              }}
              aria-label="open"
              disabled={localLinks?.length > 1}
            >
              <OpenInNewIcon
                fontSize="small"
                sx={{ color: localLinks?.length > 1 ? theme.palette.text.disabled : theme.palette.text.primary }}
              />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  )
}

export default Notification
